import Alert from '@components/alert'
import { ButtonVariant } from '@components/buttons/button'
import SimplePortableText from '@components/simple-portable-text'
import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { AttributeInput } from '@data/shopify/storefront/types'
import { isMarketingConsentGranted } from '@lib/analytics/consent'
import { AnalyticsContext } from '@lib/analytics/context'
import { getProductEventPayload } from '@lib/analytics/events'
import { AnalyticsEventName } from '@lib/analytics/types'
import { CartContext } from '@lib/cart/context'
import { getPriceNumber } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import { getFormattedProductTitle } from '@lib/product'
import { getPageUrl } from '@lib/routes'
import { ShopContext } from '@lib/shop-context'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'
import { useContext, useState } from 'react'
import ProductAdd from './product-add'
import ProductAvailabilityCountdown from './product-availability-countdown'
import ProductPreorderCountdown from './product-preorder-countdown'
import ProductWaitlist from './product-waitlist'

interface ProductActionsProps {
  title: string
  product: SanityProductFragment
  variant: SanityProductVariantFragment
  isPreorderActive: boolean
  preorderCountdownTarget?: string
  preorderDeliveryDate?: string
  isAvailabilityCountdownActive?: boolean
  availabilityCountdownTarget?: string
  enableWaitlist?: boolean
  disabled?: boolean
}

const ProductActions = ({
  title,
  product,
  variant,
  isPreorderActive,
  preorderCountdownTarget,
  preorderDeliveryDate,
  isAvailabilityCountdownActive,
  availabilityCountdownTarget,
  enableWaitlist,
  disabled,
}: ProductActionsProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { openCartAfterAddingItems, addItemsToCart, toggleCart } =
    useContext(CartContext)
  const { locale } = useContext(LanguageContext)
  const { currencyCode } = useContext(ShopContext)
  const { settings } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const [quantity] = useState(1)
  const [isAddToCartError, setIsAddToCartError] = useState(false)

  const handleAddToCart = async (customAttributes?: AttributeInput[]) => {
    setIsAddToCartError(false)

    const isSuccessful = await addItemsToCart([
      {
        id: variant.variantID,
        quantity,
        customAttributes,
      },
    ])

    setIsAddToCartError(!isSuccessful)

    if (isSuccessful) {
      // Trigger add to cart event
      triggerAnalyticsEvent(
        AnalyticsEventName.AddToCart,
        getProductEventPayload(
          title,
          [
            {
              variantId: variant.variantID,
              options: variant.options,
              price: variant.price,
              comparePrice: variant.comparePrice,
              quantity,
            },
          ],
          currencyCode,
        ),
      )

      // Klaviyo event
      if (settings && window.klaviyo && isMarketingConsentGranted()) {
        const domain = settings.siteURL.replace(/\/+$/g, '')
        const productUrl = getPageUrl(locale, 'product', product.slug.current, {
          variant: `${variant.variantID}`,
        })
        const klaviyoProductItem: KlaviyoProductItem = {
          ProductName: getFormattedProductTitle(locale, product.title),
          ProductID: `${product.productID}`,
          VariantID: `${variant.variantID}`,
          SKU: variant.sku,
          URL: `${domain}${productUrl}`,
          Price: getPriceNumber(variant.price),
        }

        if (variant.comparePrice) {
          klaviyoProductItem.CompareAtPrice = getPriceNumber(
            variant.comparePrice,
          )
        }

        window.klaviyo.track('Added To Cart', klaviyoProductItem)
      }

      // Triple Whale pixel event
      if (window.TriplePixel) {
        window.TriplePixel('AddToCart', {
          item: `${product.productID}`,
          q: quantity,
          v: `${variant.variantID}`,
        })
      }

      if (openCartAfterAddingItems) {
        toggleCart(true)
      }
    }
  }

  if (isPreorderActive && preorderCountdownTarget && preorderDeliveryDate) {
    return (
      <ProductPreorderCountdown
        preorderCountdownTarget={preorderCountdownTarget}
        preorderDeliveryDate={preorderDeliveryDate}
        disabled={disabled}
        onClick={handleAddToCart}
      />
    )
  }

  if (isAvailabilityCountdownActive && availabilityCountdownTarget) {
    return (
      <ProductAvailabilityCountdown
        availabilityCountdownTarget={availabilityCountdownTarget}
      />
    )
  }

  if (!variant.inStock) {
    if (enableWaitlist && !disabled) {
      return (
        <ProductWaitlist
          // TODO: Move to product hero component setting.
          service="klaviyo"
          variantId={variant.variantID}
        />
      )
    }

    return null
  }

  return (
    <div className="flex flex-col items-baseline w-full text-center">
      <ProductAdd
        variant={ButtonVariant.FILLED}
        onClick={handleAddToCart}
        disabled={disabled}
        className="w-full"
      >
        {strings.buttonAddToCart}
      </ProductAdd>

      {isAddToCartError && (
        <div key="error" className="mt-8 w-full">
          <Alert error className="rc rc-alert rc-error">
            <SimplePortableText content={strings.cartAddToCartErrorMessage} />
          </Alert>
        </div>
      )}
    </div>
  )
}

export default ProductActions
